/**
 * <product-tooltip> — click-toggled tooltip popup.
 * Figma node 103:980 (product page shipping-time tooltip).
 *
 * Expected markup:
 *   <product-tooltip>
 *     <button type="button" aria-expanded="false" aria-controls="ID" data-tooltip-toggle>…</button>
 *     <div id="ID" data-tooltip-panel hidden>…</div>
 *   </product-tooltip>
 *
 * Disclosure semantics (aria-expanded), not role="tooltip" — role="tooltip" describes a
 * hover/focus hint, and this one is click-toggled and stays open.
 */
(() => {
  if (window.customElements.get('product-tooltip')) return;

  const ProductTooltip = class extends HTMLElement {
    constructor() {
      super();

      this.toggle = this.querySelector('[data-tooltip-toggle]');
      this.panel = this.querySelector('[data-tooltip-panel]');
      if (!this.toggle || !this.panel) return;

      this.onToggleClick = this.onToggleClick.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      this.toggle.addEventListener('click', this.onToggleClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
    }

    get isOpen() {
      return this.toggle.getAttribute('aria-expanded') === 'true';
    }

    onToggleClick(evt) {
      evt.preventDefault();
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    }

    onDocumentClick(evt) {
      if (!this.contains(evt.target)) this.close();
    }

    onKeydown(evt) {
      if (evt.key !== 'Escape') return;
      this.close();
      this.toggle.focus();
    }

    open() {
      this.panel.removeAttribute('hidden');
      this.toggle.setAttribute('aria-expanded', 'true');
      this.keepInViewport();

      // Deferred so the click that opened the tooltip does not immediately close it.
      requestAnimationFrame(() => {
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onKeydown);
      });
    }

    close() {
      this.panel.setAttribute('hidden', '');
      this.toggle.setAttribute('aria-expanded', 'false');
      this.panel.style.removeProperty('--tooltip-shift');
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
    }

    /** Nudge the panel back on screen when edge anchoring overflows either side (LTR or RTL). */
    keepInViewport() {
      const gutter = 16;
      const rect = this.panel.getBoundingClientRect();
      const overflowLeft = gutter - rect.left;
      const overflowRight = rect.right - (window.innerWidth - gutter);
      let shift = 0;
      if (overflowLeft > 0) {
        shift = overflowLeft;
      } else if (overflowRight > 0) {
        shift = -overflowRight;
      }
      if (shift !== 0) {
        this.panel.style.setProperty('--tooltip-shift', `${Math.round(shift)}px`);
      }
    }
  };

  window.customElements.define('product-tooltip', ProductTooltip);
})();
